import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClaimReview = ({ claimData, onSubmit, onBack, onSaveDraft }) => {
  const [confirmations, setConfirmations] = useState({
    accuracy: false,
    compliance: false,
    authorization: false,
    disclosure: false
  });

  const mockClaimData = {
    claimType: 'offsetting',
    claimCode: 'VCMI-OC-2024-001',
    companyName: 'Global Carbon Corp',
    reportingYear: '2024',
    selectedCredits: [
      {
        id: 'VCS-001-2024',
        projectName: 'Amazon Forest Conservation Project',
        quantity: 1000,
        unitPrice: 12.50,
        registry: 'Verra',
        vintage: 2023
      },
      {
        id: 'GS-002-2024',
        projectName: 'Kenya Solar Cookstoves Program',
        quantity: 500,
        unitPrice: 15.75,
        registry: 'Gold Standard',
        vintage: 2023
      }
    ],
    totalQuantity: 1500,
    totalValue: 20375,
    evidenceFiles: 8,
    verificationStatus: 'complete',
    estimatedProcessingTime: '5-7 business days'
  };

  const data = claimData || mockClaimData;

  const confirmationItems = [
    {
      id: 'accuracy',
      label: 'Data Accuracy Confirmation',
      description: 'I confirm that all information provided is accurate and complete to the best of my knowledge.'
    },
    {
      id: 'compliance',
      label: 'VCMI Compliance Acknowledgment',
      description: 'I acknowledge that this claim follows VCMI Claims Code requirements and guidelines.'
    },
    {
      id: 'authorization',
      label: 'Submission Authorization',
      description: 'I am authorized to submit this claim on behalf of the organization.'
    },
    {
      id: 'disclosure',
      label: 'Transparency Agreement',
      description: 'I agree to public disclosure requirements as per VCMI transparency standards.'
    }
  ];

  const handleConfirmationChange = (id, checked) => {
    setConfirmations(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const allConfirmed = Object.values(confirmations)?.every(Boolean);

  const getClaimTypeDetails = (type) => {
    const types = {
      offsetting: {
        title: 'Offsetting Claim',
        description: 'Neutralizing residual emissions through verified carbon credit retirement',
        icon: 'Target',
        color: 'text-primary'
      },
      contribution: {
        title: 'Contribution Claim',
        description: 'Supporting climate action beyond value chain',
        icon: 'Heart',
        color: 'text-accent'
      },
      inset: {
        title: 'Inset Claim',
        description: 'Reducing emissions within value chain',
        icon: 'ArrowDownCircle',
        color: 'text-secondary'
      }
    };
    return types?.[type] || types?.offsetting;
  };

  const claimTypeInfo = getClaimTypeDetails(data?.claimType);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground mb-2">Review & Submit Claim</h2>
        <p className="text-sm text-muted-foreground">
          Please review all claim details before submission. Once submitted, changes will require a new claim process.
        </p>
      </div>
      {/* Claim Summary */}
      <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-primary rounded-lg">
            <Icon name={claimTypeInfo?.icon} size={20} className="text-primary-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-card-foreground">{claimTypeInfo?.title}</h3>
            <p className="text-sm text-muted-foreground">{claimTypeInfo?.description}</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-muted-foreground">Claim Code</div>
            <div className="font-mono text-sm font-medium">{data?.claimCode}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-card rounded-lg">
            <div className="text-2xl font-bold text-primary">{data?.totalQuantity?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">tCO2e Retired</div>
          </div>
          <div className="text-center p-3 bg-card rounded-lg">
            <div className="text-2xl font-bold text-success">${data?.totalValue?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Total Value</div>
          </div>
          <div className="text-center p-3 bg-card rounded-lg">
            <div className="text-2xl font-bold text-accent">{data?.selectedCredits?.length}</div>
            <div className="text-xs text-muted-foreground">Credit Projects</div>
          </div>
        </div>
      </div>
      {/* Company Information */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-3">Company Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Company Name:</span>
            <div className="font-medium">{data?.companyName}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Reporting Year:</span>
            <div className="font-medium">{data?.reportingYear}</div>
          </div>
        </div>
      </div>
      {/* Selected Credits */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-3">Selected Credits</h3>
        <div className="space-y-3">
          {data?.selectedCredits?.map((credit) => (
            <div key={credit?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <div className="font-medium text-card-foreground">{credit?.projectName}</div>
                <div className="text-xs text-muted-foreground">{credit?.registry} • Vintage {credit?.vintage}</div>
              </div>
              <div className="text-right">
                <div className="font-medium">{credit?.quantity?.toLocaleString()} tCO2e</div>
                <div className="text-xs text-muted-foreground">${credit?.unitPrice}/tCO2e</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Evidence Status */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-3">Evidence Status</h3>
        <div className="flex items-center space-x-4 p-3 bg-success/10 border border-success/20 rounded-lg">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <div>
            <div className="font-medium text-success">All Evidence Verified</div>
            <div className="text-xs text-success/80">{data?.evidenceFiles} documents uploaded and validated</div>
          </div>
        </div>
      </div>
      {/* Processing Information */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-3">Processing Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Estimated Processing Time:</span>
            <div className="font-medium">{data?.estimatedProcessingTime}</div>
          </div>
          <div>
            <span className="text-muted-foreground">Verification Status:</span>
            <div className="font-medium text-success capitalize">{data?.verificationStatus}</div>
          </div>
        </div>
      </div>
      {/* Confirmations */}
      <div className="mb-6">
        <h3 className="font-medium text-card-foreground mb-4">Required Confirmations</h3>
        <div className="space-y-4">
          {confirmationItems?.map((item) => (
            <Checkbox
              key={item?.id}
              label={item?.label}
              description={item?.description}
              checked={confirmations?.[item?.id]}
              onChange={(e) => handleConfirmationChange(item?.id, e?.target?.checked)}
              required
            />
          ))}
        </div>
      </div>
      {/* Submission Warning */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-warning mb-1">Important Notice</h4>
            <p className="text-xs text-warning/80">
              Once submitted, this claim cannot be modified. Please ensure all information is accurate. 
              The claim will be processed according to VCMI Claims Code requirements and may be subject to third-party verification.
            </p>
          </div>
        </div>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back to Evidence
        </Button>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={onSaveDraft} iconName="Save" iconPosition="left">
            Save Draft
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!allConfirmed}
            iconName="Send"
            iconPosition="right"
            variant="success"
          >
            Submit Claim
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClaimReview;